import {
  ArrowRight,
  Bell,
  Bug,
  ClipboardCheck,
  MessageSquare,
  Repeat,
  X,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { getCategory } from "./NotificationItem";

import type { Notification } from "@/common/models/notification.model";

type Category = ReturnType<typeof getCategory>;

interface CategoryMeta {
  Icon: LucideIcon;
  /** Solid color for the left accent bar. */
  accent: string;
  /** Tinted background + colored icon for the badge. */
  badge: string;
  /** Colored chip in the header strip. */
  chip: string;
  label: string;
}

const CATEGORY_META: Record<Category, CategoryMeta> = {
  automation: {
    Icon: Zap,
    accent: "bg-info",
    badge: "bg-info/15 text-info",
    chip: "border-info/25 bg-info/10 text-info",
    label: "Automation",
  },
  manual: {
    Icon: ClipboardCheck,
    accent: "bg-primary",
    badge: "bg-primary/15 text-primary",
    chip: "border-primary/25 bg-primary/10 text-primary",
    label: "Manual",
  },
  defect: {
    Icon: Bug,
    accent: "bg-graph-failed",
    badge: "bg-graph-failed/15 text-graph-failed",
    chip: "border-graph-failed/25 bg-graph-failed/10 text-graph-failed",
    label: "Defect",
  },
  comment: {
    Icon: MessageSquare,
    accent: "bg-info",
    badge: "bg-info/15 text-info",
    chip: "border-info/25 bg-info/10 text-info",
    label: "Comment",
  },
  cycle: {
    Icon: Repeat,
    accent: "bg-graph-in-progress",
    badge: "bg-graph-in-progress/15 text-graph-in-progress",
    chip: "border-graph-in-progress/25 bg-graph-in-progress/10 text-graph-in-progress",
    label: "Cycle",
  },
  system: {
    Icon: Bell,
    accent: "bg-primary",
    badge: "bg-primary/15 text-primary",
    chip: "border-primary/25 bg-primary/10 text-primary",
    label: "System",
  },
};

interface NotificationToastProps {
  notification: Notification;
  onClick: () => void;
  onDismiss: () => void;
}

const NotificationToast = ({
  notification,
  onClick,
  onDismiss,
}: NotificationToastProps) => {
  const meta = CATEGORY_META[getCategory(notification.eventType)];
  const { Icon } = meta;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className="group relative w-[360px] cursor-pointer overflow-hidden rounded-xl border border-border bg-card shadow-xl outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      {/* Category accent bar — a strong, notification-only visual signature */}
      <span
        className={["absolute inset-y-0 left-0 w-1", meta.accent].join(" ")}
        aria-hidden
      />

      {/* Header strip — the "New notification" label makes it unmistakably a notification */}
      <div className="flex items-center justify-between gap-2 border-b border-border/70 bg-muted/40 py-1.5 pl-4 pr-2.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Bell
            size={11}
            className="shrink-0 text-muted-foreground"
            strokeWidth={2}
          />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            New notification
          </span>
          <span
            className={[
              "inline-flex h-4 shrink-0 items-center rounded border px-1.5 text-[9px] font-semibold uppercase tracking-wide",
              meta.chip,
            ].join(" ")}
          >
            {meta.label}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="text-[10px] font-medium text-muted-foreground tabular-nums">
            Just now
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            aria-label="Dismiss"
            className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex items-start gap-3 py-2.5 pl-4 pr-3">
        <span
          className={[
            "mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg",
            meta.badge,
          ].join(" ")}
        >
          <Icon size={16} strokeWidth={1.9} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-semibold leading-4 text-foreground">
            {notification.title}
          </p>
          {notification.message && (
            <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-muted-foreground">
              {notification.message}
            </p>
          )}
          <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
            View
            <ArrowRight
              size={12}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default NotificationToast;
